import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post, setVia, type Location, type Me } from './api';
import { SignIn } from './SignIn';
import { EmployeesPage } from './EmployeesPage';
import { AccessPage } from './AccessPage';
import { DayBoardPage } from './DayBoard';
import { DevicePage } from './DevicePage';
import { AvailNotice } from './AvailabilityModal';
import { EmployeeApp } from './EmployeeApp';
import { StaffMessages, useUnread } from './Messages';
import { SettingsPage } from './SettingsPage';
import { StoreTab } from './StoreTab';
import { StatusPage } from './StatusPage';
import { DemoOutbox } from './DemoOutbox';
import { useStickyNav } from './ui';
import { HelpPage, helpFor, startFor } from './HelpPage';
import { AllStoresPage } from './AllStoresPage';
import { ItemsPage } from './ItemsPage';
import { ReportsPage } from './ReportsPage';
import { ReceiptLookup } from './ReceiptLookup';

/** A phone-sized screen (or the /store link) gets the phone app; "Open the desktop pages" sticks per device. */
const NARROW = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;
const deskPref = () => { try { return localStorage.getItem('mw.desk') === '1'; } catch { return false; } };
const setDeskPref = (on: boolean) => { try { if (on) localStorage.setItem('mw.desk', '1'); else localStorage.removeItem('mw.desk'); } catch { /* fine */ } };

type Page = 'day' | 'rotation' | 'week' | 'employees' | 'messages' | 'settings' | 'reports' | 'rweek' | 'rhistory' | 'rsettings' | 'rcinema' | 'rall' | 'ritems' | 'receipts' | 'access' | 'status' | 'help';
/** The Reports app's pages (owner 10/27; week summary, history and settings 10/30). */
const REPORT_PAGES: Page[] = ['reports', 'rweek', 'rhistory', 'rcinema', 'rall', 'ritems', 'rsettings', 'receipts'];
const REPORT_VIEW: Partial<Record<Page, 'daily' | 'week' | 'history' | 'cinema' | 'settings'>> = { reports: 'daily', rweek: 'week', rhistory: 'history', rcinema: 'cinema', rsettings: 'settings' };
const PATHS: Record<Page, string> = { day: '/day', rotation: '/rotation', messages: '/messages', settings: '/settings', week: '/week-summary', employees: '/employees', access: '/admin/access', status: '/admin/status', help: '/help', reports: '/reports', rweek: '/reports/week', rhistory: '/reports/history', rsettings: '/reports/settings', rcinema: '/reports/cinema', rall: '/admin/all-stores', ritems: '/admin/items', receipts: '/reports/receipts' };

function pageFromPath(): Page {
  if (location.pathname.startsWith('/admin/access')) return 'access';
  if (location.pathname.startsWith('/admin/status')) return 'status';
  if (location.pathname.startsWith('/week-summary')) return 'week';
  if (location.pathname.startsWith('/rotation')) return 'rotation';
  if (location.pathname.startsWith('/messages')) return 'messages';
  if (location.pathname.startsWith('/help')) return 'help';
  if (location.pathname.startsWith('/reports/receipts')) return 'receipts';
  if (location.pathname.startsWith('/reports/week')) return 'rweek';
  if (location.pathname.startsWith('/reports/history')) return 'rhistory';
  if (location.pathname.startsWith('/reports/settings')) return 'rsettings';
  if (location.pathname.startsWith('/reports/cinema')) return 'rcinema';
  if (location.pathname.startsWith('/admin/all-stores')) return 'rall';
  if (location.pathname.startsWith('/admin/items')) return 'ritems';
  if (location.pathname.startsWith('/reports')) return 'reports';
  if (location.pathname.startsWith('/settings') || location.pathname.startsWith('/admin/settings')) return 'settings';
  return location.pathname.startsWith('/employees') ? 'employees' : 'day';
}

/**
 * The real app's frame. Shared devices (office computer, counter screen) get their own screens;
 * everyone else signs in with their mobile number and PIN.
 */
export function App() {
  const [state, setState] = useState<'loading' | 'device' | 'signin' | 'in'>('loading');
  const [me, setMe] = useState<Me | null>(null);

  const load = useCallback(async () => {
    if (location.pathname.startsWith('/device')) { setState('device'); return; }
    try {
      const d = await get<{ device: unknown }>('/api/device');
      if (d.device) { setState('device'); return; }
    } catch { /* not a device */ }
    try {
      const r = await get<{ me: Me }>('/api/auth/me');
      setMe(r.me); setState('in');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setState('signin');
      else setState('signin');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (state === 'loading') return null;
  if (state === 'device') return <DevicePage />;
  if (state === 'signin' || !me) {
    return <div className="approot"><main className="signwrap"><SignIn onSignedIn={(m) => { setMe(m); setState('in'); }} /></main></div>;
  }
  const signOut = async () => { await post('/api/auth/logout'); setMe(null); setState('signin'); history.replaceState(null, '', '/'); };
  // Employees get the phone app; shift leads too, with the Store tab. Managers, admins and HR get the
  // desktop pages, or the phone app with the Store tab on a phone (docs/sign-in.md "Phone Store tab").
  if (me.role === 'employee') return <EmployeeApp me={me} onMe={setMe} onSignOut={signOut} />;
  if (me.role === 'lead') return <Phone><EmployeeApp me={me} onMe={setMe} onSignOut={signOut} storeTab /></Phone>;
  const toDesk = () => { setDeskPref(true); history.replaceState(null, '', '/day'); location.reload(); };
  if ((NARROW() && !deskPref()) || location.pathname.startsWith('/store')) {
    if (location.pathname.startsWith('/store')) setDeskPref(false);
    return <Phone>{me.employeeId
      ? <EmployeeApp me={me} onMe={setMe} onSignOut={signOut} storeTab onDesktop={toDesk} />
      : <PhoneStore me={me} onSignOut={signOut} onDesktop={toDesk} />}</Phone>;
  }
  return <Signed me={me} onSignOut={signOut} />;
}

/** Requests from the phone app are marked, so changes made there are recorded as "(phone)". */
function Phone({ children }: { children: React.ReactNode }) {
  setVia('phone');
  return <>{children}</>;
}

/** Admins and HR who aren't on an employee list: the phone opens straight to the Store tab. */
function PhoneStore({ me, onSignOut, onDesktop }: { me: Me; onSignOut: () => void; onDesktop: () => void }) {
  const [help, setHelp] = useState(false);
  return (
    <div className="approot" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main className="empmain" style={{ display: 'flex', flexDirection: 'column', width: '100%', boxSizing: 'border-box' }}>
        <div className="eappbar">
          <span className="eabrand brand" aria-label="MyWunderland Schedule"><span>My<span className="bw">Wunderland</span></span><span className="bdiv" aria-hidden="true" /><span className="bapp">Schedule</span></span>
          <span className="eastore">{me.role === 'hr' ? 'HR' : 'Admin'} · {me.name}</span>
          <button className="elink" onClick={() => setHelp((v) => !v)} aria-pressed={help}>Help</button>
          <button className="elink" onClick={onSignOut}>Sign out</button>
        </div>
        {help ? <HelpPage role={me.role} where="phone" hasEmployee={false} start="store-tab" onClose={() => setHelp(false)} />
          : <StoreTab me={me} onDesktop={onDesktop} />}
      </main>
    </div>
  );
}

/** Signed in on their own device. Managers, admins and HR get the desktop pages built so far. */
function Signed({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const [page, setPage] = useState<Page>(pageFromPath());
  const [locs, setLocs] = useState<Location[]>([]);
  const [loc, setLoc] = useState<number | null>(null);

  const [locsVer, setLocsVer] = useState(0);
  useEffect(() => {
    get<{ locations: Location[] }>('/api/locations').then((r) => {
      setLocs(r.locations);
      let saved: number | null = null;
      try { saved = Number(localStorage.getItem('mw.loc')) || null; } catch { /* private window */ }
      const pick = r.locations.find((l) => l.id === saved) ?? r.locations.find((l) => l.id === me.homeLocationId) ?? r.locations[0];
      setLoc(pick?.id ?? null);
    }).catch(() => setLocs([]));
  }, [me.homeLocationId, locsVer]);
  useEffect(() => {
    const pop = () => setPage(pageFromPath());
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, []);
  // admins change settings, so their Settings page sits under /admin (Cloudflare's admin lock); others only look
  const [helpFrom, setHelpFrom] = useState<Page | null>(null);
  const navRef = useStickyNav<HTMLElement>();
  const go = (p: Page) => { history.pushState(null, '', p === 'settings' && me.role === 'admin' ? '/admin/settings' : PATHS[p]); setPage(p); };
  const pickLoc = (id: number) => { setLoc(id); try { localStorage.setItem('mw.loc', String(id)); } catch { /* fine */ } };

  const desk = me.role === 'admin' || me.role === 'hr' || me.role === 'manager';
  const isOffice = locs.find((l) => l.id === loc)?.kind === 'office';
  const isDemo = !!locs.find((l) => l.id === loc)?.demo;
  const live = locs.find((l) => l.id === loc)?.demoMode === 'live';
  const [outbox, setOutbox] = useState(false);
  const [unread, refreshUnread] = useUnread(desk);
  const tabs: [Page, string][] = isOffice ? [['employees', 'Employees']] : [['day', 'Day board'], ['rotation', 'Rotation'], ['week', 'Week summary'], ['employees', 'Employees']];
  const nMsg = unread.staff + unread.hr;
  tabs.push(['messages', nMsg ? `Messages (${nMsg})` : 'Messages']);
  if (!isOffice) tabs.push(['settings', 'Settings']);
  // Reports (owner 10/27): store managers (levels 7–9) and admins; HR doesn't use Reports
  const reportsOk = me.role === 'admin' || (me.role === 'manager' && (me.level ?? 0) >= 7);
  const receiptsOk = me.role === 'admin' || (me.role === 'manager' && (me.level ?? 0) >= 9);   // owner 10/29
  // admin pages (owner 10/30): on EVERY area's tab bar (Schedule, Reports and any added later), so admins never switch to reach them
  const adminTabs: [Page, string][] = me.role === 'admin' ? [['access', 'Managers & access'], ['status', 'System status']] : [];
  tabs.push(...adminTabs);
  const current = (page === 'access' || page === 'status') && me.role !== 'admin' ? 'employees' : (page === 'day' || page === 'week' || page === 'rotation' || page === 'settings' || REPORT_PAGES.includes(page)) && isOffice ? 'employees' : REPORT_PAGES.includes(page) && !reportsOk ? 'day' : (page === 'receipts' || page === 'rhistory') && !receiptsOk ? 'reports' : (page === 'rall' || page === 'ritems') && me.role !== 'admin' ? 'reports' : page;
  const openHelp = () => { if (current !== 'help') setHelpFrom(current); go('help'); };
  // Schedule / Reports switch next to the name (owner 10/27, Reports prototype): Reports has its own tabs.
  const canReports = reportsOk && !isOffice && locs.some((l) => l.kind !== 'office');
  // pages every area shares (admin pages, Help) keep the tab bar of the area they were opened from
  const SHARED: Page[] = ['access', 'status', 'help'];
  const [area, setArea] = useState<'schedule' | 'reports'>(() => { try { return sessionStorage.getItem('mw.area') === 'reports' ? 'reports' : 'schedule'; } catch { return 'schedule'; } });
  useEffect(() => {
    const a = REPORT_PAGES.includes(current) ? 'reports' : SHARED.includes(current) ? null : 'schedule';
    if (a) { setArea(a); try { sessionStorage.setItem('mw.area', a); } catch { /* fine */ } }
  }, [current]);
  const inReports = REPORT_PAGES.includes(current) || (SHARED.includes(current) && area === 'reports' && canReports);
  const [appMenu, setAppMenu] = useState(false);
  const [lastSchedule, setLastSchedule] = useState<Page>('day');
  useEffect(() => { if (!REPORT_PAGES.includes(current) && !SHARED.includes(current)) setLastSchedule(current); }, [current]);
  useEffect(() => {
    if (!appMenu) return;
    const close = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest('.appmenu, .appswitch')) setAppMenu(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAppMenu(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [appMenu]);
  // Reports tabs (owner 10/30, as in the Reports prototype): History is for level 9 managers and admins, like Find a receipt
  const shownTabs: [Page, string][] = inReports ? [['reports', 'Daily paperwork'], ['rweek', 'Week summary'], ...(receiptsOk ? [['rhistory', 'History'], ['receipts', 'Find a receipt']] as [Page, string][] : []), ['rcinema', 'Cinema'], ...(me.role === 'admin' ? [['rall', 'All stores'], ['ritems', 'Items sold']] as [Page, string][] : []), ['rsettings', 'Settings'], ...adminTabs] : tabs;

  const allStoresPage = current === 'access' || current === 'status';
  const locOff = locs.length < 2 || allStoresPage;
  return (
    <div className="approot" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header className="noprint" style={{ background: '#17212B', color: '#FFFFFF', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px 28px', padding: '14px 28px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div className="brand" style={{ fontSize: 20, position: 'relative' }} aria-label={`MyWunderland ${inReports ? 'Reports' : 'Schedule'}`}>
            <span>My<span className="bw">Wunderland</span></span><span className="bdiv" aria-hidden="true" />
            {canReports
              ? <button className="bapp appswitch" aria-expanded={appMenu} aria-haspopup="menu" onClick={() => setAppMenu((m) => !m)}>{inReports ? 'Reports' : 'Schedule'} <span className="appcaret">▾</span></button>
              : <span className="bapp">Schedule</span>}
            {appMenu && (
              <div className="appmenu" role="menu">
                <button role="menuitem" className={inReports ? '' : 'on'} onClick={() => { setAppMenu(false); if (inReports) go(lastSchedule); }}><b>Schedule{inReports ? '' : ' ✓'}</b><span className="sub">Day board, rotation, employees</span></button>
                <button role="menuitem" className={inReports ? 'on' : ''} onClick={() => { setAppMenu(false); if (!inReports) go('reports'); }}><b>Reports{inReports ? ' ✓' : ''}</b><span className="sub">Daily paperwork, weekly totals, history</span></button>
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#B9C2CB' }}>Signed in as {me.name}</div>
        </div>
        <div style={{ flex: '1 1 0' }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 20px' }}>
          {desk && locs.length > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: locOff ? '#8A96A2' : '#D5DCE2' }}
              title={allStoresPage ? 'This page covers every store, so Location isn’t used here.' : locs.length < 2 ? 'You manage one store.' : undefined}>Location
              <select value={loc ?? ''} onChange={(e) => pickLoc(Number(e.target.value))} disabled={locOff} aria-label="Location"
                style={locOff ? { background: '#2E3A45', color: '#8A96A2', borderColor: '#46525E', cursor: 'not-allowed', opacity: 1 } : undefined}>
                {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
          )}
          {isDemo && current !== 'access' && current !== 'status' && <>
            {live
              ? <span className="demotag live" title="A made-up store for live tests. Made-up people are never texted, but a real mobile number put on one of its people gets real texts, and its emails really go out.">Live Demo · real numbers get real texts</span>
              : <span className="demotag" title="A made-up store for trying things out. Nothing is ever texted or emailed from it, not even to a real number: it all shows in the Demo outbox.">Demo store · nothing is texted</span>}
            <button className="tbtn" onClick={() => setOutbox(true)}>Demo outbox</button>
          </>}
          {NARROW() && <button className="tbtn" onClick={() => { setDeskPref(false); history.replaceState(null, '', '/store'); location.reload(); }}>Phone view</button>}
          <button className="tbtn" onClick={onSignOut}>Sign out</button>
        </div>
      </header>
      {desk && (
        <nav ref={navRef} className="noprint tabnav" style={{ background: '#FFFFFF', borderBottom: '1px solid #D9DCD6', padding: '0 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }} aria-label="Sections">
          {shownTabs.map(([p, label]) => <button key={p} className={'tabbtn' + (current === p ? ' on' : '')} onClick={() => go(p)}>{label}</button>)}
          <button className={'tabbtn helptab' + (current === 'help' ? ' on' : '')} onClick={openHelp} title="How this page works">Help</button>
          <div style={{ flex: '1 1 0' }} />
          <div id="navslot" style={{ display: current === 'day' || current === 'week' || current === 'rotation' ? 'contents' : 'none' }} />
        </nav>
      )}
      {me.role === 'hr' && <div className="noprint" style={{ background: '#FCEFD9', color: '#7A4A06', padding: '10px 28px', fontSize: 14 }}>HR view: you can see everything but not change it.</div>}
      <main className="page">
        {me.employeeId && <AvailNotice />}
        {!desk && (
          <div className="comingsoon">Hi {me.firstName}. Your schedule, availability and messages will show here soon. You’re signed in and all set.</div>
        )}
        {desk && (current === 'day' || current === 'week' || current === 'rotation') && loc !== null && locs.length > 0 && <DayBoardPage key={loc} me={me} locationId={loc} view={current} />}
        {desk && current === 'employees' && loc !== null && <EmployeesPage me={me} locationId={loc} locationName={locs.find((l) => l.id === loc)?.name ?? ''} />}
        {desk && current === 'messages' && loc !== null && <StaffMessages me={me} locationId={loc} onSeen={refreshUnread} />}
        {desk && current === 'settings' && loc !== null && <SettingsPage key={loc} me={me} locationId={loc} />}
        {desk && current === 'status' && <StatusPage />}
        {desk && REPORT_VIEW[current] && <ReportsPage key={loc ?? 0} locations={locs} locationId={loc} view={REPORT_VIEW[current]!} onView={(v) => go(v === 'daily' ? 'reports' : v === 'week' ? 'rweek' : v === 'history' ? 'rhistory' : v === 'cinema' ? 'rcinema' : 'rsettings')} />}
        {desk && current === 'ritems' && me.role === 'admin' && <ItemsPage stores={locs.filter((l) => l.kind !== 'office' && !l.demo).map((l) => ({ id: l.id, name: l.name }))} />}
        {desk && current === 'rall' && me.role === 'admin' && <AllStoresPage stores={locs.filter((l) => l.kind !== 'office' && !l.demo).map((l) => ({ id: l.id, name: l.name }))} />}
        {desk && current === 'receipts' && <ReceiptLookup locations={locs} locationId={loc} />}
        {desk && current === 'help' && <HelpPage role={me.role} where="desk" hasEmployee={!!me.employeeId}
          start={helpFrom ? startFor(helpFor(me.role, 'desk', !!me.employeeId), helpFrom) : undefined} />}
        {outbox && loc !== null && <DemoOutbox locationId={loc} onClose={() => setOutbox(false)} />}
        {desk && current === 'access' && <AccessPage locations={locs} onLocationsChanged={() => setLocsVer((v) => v + 1)} />}
      </main>
    </div>
  );
}
